'use client'

import { useMemo, useRef, useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { StorageRack, StorageLevel, StorageNode, StorageAllocation } from '@garage/shared'
import {
  PageHeader,
  Button,
  IconButton,
  TextField,
  EmptyState,
  Skeleton,
  ConfirmDialog,
  ChevronDownIcon,
  AddIcon,
  EditIcon,
  DeleteIcon,
  SearchIcon,
  InventoryIcon,
  QrCodeIcon,
  useSnackbar,
} from '@/ui'
import { StatCard } from '@/components/StatCard.js'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useDebounced, useFormatters } from '@/lib/hooks.js'
import { NodeFormDialog } from '@/components/storage/NodeFormDialog.js'
import { AllocationFormDialog } from '@/components/storage/AllocationFormDialog.js'
import { AllocationDetailDialog } from '@/components/storage/AllocationDetailDialog.js'
import { AllocationIcon } from '@/components/storage/AllocationIcon.js'
import { AllocationSortSelect, useAllocationSort } from '@/components/storage/AllocationSortSelect.js'
import { sortAllocations } from '@/components/storage/allocationSort.js'
import { LevelQrDialog } from '@/components/storage/LevelQrDialog.js'

type NodeDialogState = { open: false } | { open: true; kindLabel: string; node?: StorageNode; parentId: string | null }
type AllocationDialogState = { open: false } | { open: true; levelId: string; allocation?: StorageAllocation }
// Only the id, not the allocation itself — looked up fresh from the live
// query below on every render, so removing/adding a photo (or any other
// edit) while this dialog is open shows up immediately instead of the
// dialog holding a stale snapshot from the moment it was opened.
type DetailState = { levelId: string; allocationId: string } | null
type DeleteTarget =
  | { kind: 'node'; id: string; label: string }
  | { kind: 'allocation'; id: string; label: string }
  | null

/** One allocation, with the rack/level it lives on — the shape search and "recently added" both work from. */
interface LocatedAllocation {
  allocation: StorageAllocation
  rack: StorageRack
  level: StorageLevel
}

function flattenAllocations(racks: StorageRack[]): LocatedAllocation[] {
  const out: LocatedAllocation[] = []
  for (const rack of racks) {
    for (const level of rack.levels) {
      for (const allocation of level.allocations) {
        out.push({ allocation, rack, level })
      }
    }
  }
  return out
}

/** A search match: either a specific item, or — when nothing on the level matched by name but the LEVEL itself did — the location alone. */
interface SearchHit {
  rack: StorageRack
  level: StorageLevel
  allocation: StorageAllocation | null
}

/**
 * Searches item names/notes AND rack/level names. Matching on location name
 * alone matters because a level can be named and photographed before
 * anything is allocated to it yet — flattenAllocations() would never surface
 * that level at all, since it has no allocations to iterate.
 */
function searchTree(racks: StorageRack[], query: string): SearchHit[] {
  const hits: SearchHit[] = []
  for (const rack of racks) {
    for (const level of rack.levels) {
      const locationMatches = rack.name.toLowerCase().includes(query) || level.name.toLowerCase().includes(query)

      if (locationMatches) {
        if (level.allocations.length === 0) {
          hits.push({ rack, level, allocation: null })
        } else {
          for (const allocation of level.allocations) hits.push({ rack, level, allocation })
        }
        continue
      }

      for (const allocation of level.allocations) {
        if (allocation.itemName.toLowerCase().includes(query) || allocation.notes?.toLowerCase().includes(query)) {
          hits.push({ rack, level, allocation })
        }
      }
    }
  }
  return hits
}

/**
 * Where parts and liquids physically live: racks, each holding levels
 * directly — separate from the Catalog price book. A level holds a list of
 * items allocated to it; each item can carry its own photo. The search bar
 * and "Recently added" panel both work off one flattened view of every
 * allocation, so finding "what is where" doesn't need a separate server
 * round-trip — the whole tree is already in hand.
 */
export function StoragePage() {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const formatters = useFormatters()
  const { t } = useTranslation()

  const { data, isLoading, isError } = useQuery({ queryKey: qk.storageTree, queryFn: api.storage.tree })
  const racks = data?.items ?? []

  // Only one rack open at a time — picking a new one closes whichever was open.
  const [expandedRackId, setExpandedRackId] = useState<string | null>(null)
  const toggleRack = (id: string) => setExpandedRackId((prev) => (prev === id ? null : id))

  // Only one level's contents previewed at a time, same as racks — picking a
  // new one closes whichever was open instead of stacking several at once.
  const [expandedLevelId, setExpandedLevelId] = useState<string | null>(null)
  const toggleLevel = (id: string) => setExpandedLevelId((prev) => (prev === id ? null : id))

  const [searchInput, setSearchInput] = useState('')
  const searchQuery = useDebounced(searchInput, 200).trim().toLowerCase()

  // Which level to scroll to and briefly highlight, set by clicking a search
  // or "recently added" result — those only show a location, so jumping to
  // it in the tree below is what actually answers "where is it".
  const [focusLevelId, setFocusLevelId] = useState<string | null>(null)
  const levelRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  useEffect(() => {
    if (!focusLevelId) return
    const el = levelRefs.current.get(focusLevelId)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const timer = setTimeout(() => setFocusLevelId(null), 2000)
    return () => clearTimeout(timer)
  }, [focusLevelId])

  const jumpTo = (located: { rack: StorageRack; level: StorageLevel }) => {
    setSearchInput('')
    setExpandedRackId(located.rack.id)
    setExpandedLevelId(located.level.id)
    setFocusLevelId(located.level.id)
  }

  const flat = useMemo(() => flattenAllocations(racks), [racks])

  const searchResults = useMemo(() => {
    if (!searchQuery) return []
    return searchTree(racks, searchQuery)
  }, [racks, searchQuery])

  const recentlyAdded = useMemo(
    () => [...flat].sort((a, b) => b.allocation.createdAt.localeCompare(a.allocation.createdAt)).slice(0, 6),
    [flat],
  )

  const stats = useMemo(
    () => ({
      racks: racks.length,
      levels: racks.reduce((n, r) => n + r.levels.length, 0),
      items: flat.length,
      totalQuantity: flat.reduce((n, x) => n + x.allocation.quantity, 0),
    }),
    [racks, flat],
  )

  const [nodeDialog, setNodeDialog] = useState<NodeDialogState>({ open: false })
  const [allocationDialog, setAllocationDialog] = useState<AllocationDialogState>({ open: false })
  const [detail, setDetail] = useState<DetailState>(null)
  const detailAllocation = detail ? (flat.find((x) => x.allocation.id === detail.allocationId)?.allocation ?? null) : null
  const [qrLevel, setQrLevel] = useState<StorageLevel | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null)

  const deleteMutation = useMutation({
    mutationFn: (target: NonNullable<DeleteTarget>) =>
      target.kind === 'node' ? api.storage.removeNode(target.id) : api.storage.removeAllocation(target.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.storageTree })
      snackbar.success(t('storage.removed'))
      setDeleteTarget(null)
    },
    onError: (error) => {
      snackbar.error(error instanceof ApiError ? error.message : t('storage.failedToRemove'))
    },
  })

  const childProps = {
    expandedLevelId,
    onToggleLevel: toggleLevel,
    levelRefs,
    focusLevelId,
    onAddLevel: (rackId: string) => setNodeDialog({ open: true, kindLabel: t('storage.level'), parentId: rackId }),
    onEditRack: (rack: StorageRack) => setNodeDialog({ open: true, kindLabel: t('storage.rack'), node: rack, parentId: null }),
    onDeleteRack: (rack: StorageRack) => setDeleteTarget({ kind: 'node', id: rack.id, label: rack.name }),
    onEditLevel: (level: StorageLevel) => setNodeDialog({ open: true, kindLabel: t('storage.level'), node: level, parentId: null }),
    onDeleteLevel: (level: StorageLevel) => setDeleteTarget({ kind: 'node', id: level.id, label: level.name }),
    onAddItem: (levelId: string) => setAllocationDialog({ open: true, levelId }),
    onOpenItem: (levelId: string, allocation: StorageAllocation) =>
      setDetail({ levelId, allocationId: allocation.id }),
    onShowQr: (level: StorageLevel) => setQrLevel(level),
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('storage.title')}
        subtitle={t('storage.subtitle')}
        actions={
          <Button
            variant="filled"
            icon={<AddIcon className="w-5 h-5" />}
            onClick={() => setNodeDialog({ open: true, kindLabel: t('storage.rack'), parentId: null })}
          >
            {t('storage.addRack')}
          </Button>
        }
      />

      <TextField
        label={t('common.search')}
        value={searchInput}
        onChange={setSearchInput}
        placeholder={t('storage.searchPlaceholder')}
        leading={<SearchIcon className="w-4 h-4" />}
        fullWidth
      />

      {isLoading ? (
        <Skeleton lines={4} />
      ) : isError ? (
        <EmptyState title={t('storage.errorLoading')} description={t('clients.tryRefreshing')} />
      ) : searchQuery ? (
        <SearchResults results={searchResults} query={searchInput} onJumpTo={jumpTo} />
      ) : racks.length === 0 ? (
        <EmptyState
          icon={<InventoryIcon className="w-12 h-12 text-on-surface-variant" />}
          title={t('storage.noRacksYet')}
          description={t('storage.addRackDescription')}
          action={
            <Button variant="tonal" onClick={() => setNodeDialog({ open: true, kindLabel: t('storage.rack'), parentId: null })}>
              {t('storage.addARack')}
            </Button>
          }
        />
      ) : (
        <>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            <StatCard label={t('storage.racks')} value={stats.racks} />
            <StatCard label={t('storage.levels')} value={stats.levels} />
            <StatCard label={t('storage.items')} value={stats.items} />
            <StatCard label={t('storage.totalQuantity')} value={stats.totalQuantity} />
          </div>

          {recentlyAdded.length > 0 && (
            <div className="bg-surface-container border border-outline-variant rounded-lg p-4">
              <div className="text-label-md text-on-surface-variant mb-2">{t('storage.recentlyAdded')}</div>
              <div className="space-y-1">
                {recentlyAdded.map((x) => (
                  <button
                    key={x.allocation.id}
                    type="button"
                    onClick={() => jumpTo(x)}
                    className="w-full flex flex-col items-start gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3 px-2 py-2 sm:py-1.5 rounded-md hover:bg-surface-high interactive text-start"
                  >
                    <div className="min-w-0 flex items-baseline gap-2">
                      <span className="text-body-sm text-on-surface truncate">{x.allocation.itemName}</span>
                      <span className="text-body-sm text-on-surface-variant flex-shrink-0">× {x.allocation.quantity}</span>
                    </div>
                    <div className="flex items-baseline gap-3 sm:flex-shrink-0">
                      <span className="text-body-sm text-on-surface-variant">
                        {x.rack.name} › {x.level.name}
                      </span>
                      <span className="text-body-sm text-on-surface-subtle">
                        {formatters.dateTime(x.allocation.createdAt)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {racks.map((rack) => (
              <RackTile
                key={rack.id}
                rack={rack}
                selected={expandedRackId === rack.id}
                onClick={() => toggleRack(rack.id)}
                onEdit={() => childProps.onEditRack(rack)}
                onDelete={() => childProps.onDeleteRack(rack)}
              />
            ))}
          </div>

          {(() => {
            const openRack = racks.find((r) => r.id === expandedRackId)
            return openRack ? <RackPanel rack={openRack} {...childProps} /> : null
          })()}
        </>
      )}

      {nodeDialog.open && (
        <NodeFormDialog
          open
          kindLabel={nodeDialog.kindLabel}
          node={nodeDialog.node}
          parentId={nodeDialog.parentId}
          onClose={() => setNodeDialog({ open: false })}
        />
      )}

      {allocationDialog.open && (
        <AllocationFormDialog
          open
          levelId={allocationDialog.levelId}
          allocation={allocationDialog.allocation}
          onClose={() => setAllocationDialog({ open: false })}
        />
      )}

      <AllocationDetailDialog
        open={!!detail}
        allocation={detailAllocation}
        onClose={() => setDetail(null)}
        onEdit={() => {
          if (detail && detailAllocation) {
            setAllocationDialog({ open: true, levelId: detail.levelId, allocation: detailAllocation })
          }
          setDetail(null)
        }}
        onDelete={() => {
          if (detailAllocation) {
            setDeleteTarget({ kind: 'allocation', id: detailAllocation.id, label: detailAllocation.itemName })
          }
          setDetail(null)
        }}
      />

      <LevelQrDialog level={qrLevel} onClose={() => setQrLevel(null)} />

      <ConfirmDialog
        open={!!deleteTarget}
        title={deleteTarget?.kind === 'allocation' ? t('storage.removeItemTitle') : t('storage.removeThisTitle')}
        message={
          deleteTarget?.kind === 'allocation'
            ? t('storage.removeItemMessage', { name: deleteTarget.label })
            : t('storage.removeNodeMessage', { name: deleteTarget?.label })
        }
        confirmLabel={t('common.remove')}
        cancelLabel={t('common.cancel')}
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget)
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}

function SearchResults({
  results,
  query,
  onJumpTo,
}: {
  results: SearchHit[]
  query: string
  onJumpTo: (x: SearchHit) => void
}) {
  const { t } = useTranslation()
  if (results.length === 0) {
    return <EmptyState title={t('storage.noMatches')} description={t('storage.nothingFoundFor', { query })} />
  }

  return (
    <div className="bg-surface-container border border-outline-variant rounded-lg divide-y divide-outline-variant">
      {results.map((x, i) => (
        <button
          key={x.allocation?.id ?? `${x.level.id}-${i}`}
          type="button"
          onClick={() => onJumpTo(x)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-surface-high interactive text-start"
        >
          <div className="min-w-0">
            {x.allocation ? (
              <>
                <div className="text-body-md text-on-surface font-medium">
                  {x.allocation.itemName}
                  <span className="text-body-sm text-on-surface-variant font-normal ms-2">
                    × {x.allocation.quantity}
                  </span>
                </div>
                {x.allocation.notes && (
                  <div className="text-body-sm text-on-surface-variant">{x.allocation.notes}</div>
                )}
              </>
            ) : (
              <div className="text-body-md text-on-surface font-medium">
                {x.level.name}
                <span className="text-body-sm text-on-surface-variant font-normal ms-2">— {t('storage.nothingAllocatedYetShort')}</span>
              </div>
            )}
          </div>
          <div className="text-body-sm text-on-surface-variant flex-shrink-0">
            {x.rack.name} › {x.level.name}
          </div>
        </button>
      ))}
    </div>
  )
}

interface ChildProps {
  expandedLevelId: string | null
  onToggleLevel: (id: string) => void
  levelRefs: React.MutableRefObject<Map<string, HTMLDivElement>>
  focusLevelId: string | null
  onAddLevel: (rackId: string) => void
  onEditRack: (rack: StorageRack) => void
  onDeleteRack: (rack: StorageRack) => void
  onEditLevel: (level: StorageLevel) => void
  onDeleteLevel: (level: StorageLevel) => void
  onAddItem: (levelId: string) => void
  onOpenItem: (levelId: string, allocation: StorageAllocation) => void
  onShowQr: (level: StorageLevel) => void
}

interface RackTileProps {
  rack: StorageRack
  selected: boolean
  onClick: () => void
  onEdit: () => void
  onDelete: () => void
}

/** A rack, shown as a tile — the top-level "what have I got" view. */
function RackTile({ rack, selected, onClick, onEdit, onDelete }: RackTileProps) {
  const { t } = useTranslation()
  const levelCount = rack.levels.length
  const itemCount = rack.levels.reduce((n, l) => n + l.allocations.length, 0)

  return (
    <div
      className={`group relative border rounded-lg p-4 text-start interactive cursor-pointer ${
        selected ? 'border-primary bg-surface-high' : 'border-outline-variant bg-surface-container hover:bg-surface-high'
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-body-lg font-medium text-on-surface truncate">{rack.name}</div>
          {rack.description && (
            <div className="text-body-sm text-on-surface-variant truncate mt-0.5">{rack.description}</div>
          )}
        </div>
        <div className="flex gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 interactive">
          <IconButton
            icon={<EditIcon className="w-3.5 h-3.5" />}
            label={t('storage.editRack')}
            variant="standard"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onEdit()
            }}
          />
          <IconButton
            icon={<DeleteIcon className="w-3.5 h-3.5" />}
            label={t('storage.removeRack')}
            variant="danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
          />
        </div>
      </div>
      <div className="text-body-sm text-on-surface-variant mt-3">
        {t('storage.levelCount', { count: levelCount })} · {t('storage.itemCount', { count: itemCount })}
      </div>
    </div>
  )
}

/** The detail for the one open rack: its levels as tiles, plus the one level opened within it. */
function RackPanel({ rack, ...props }: { rack: StorageRack } & ChildProps) {
  const { t } = useTranslation()
  const { expandedLevelId, onToggleLevel } = props
  const openLevel = rack.levels.find((l) => l.id === expandedLevelId)

  return (
    <div className="bg-surface-container border border-outline-variant rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-body-lg font-medium text-on-surface">{rack.name}</div>
        <Button variant="tonal" size="sm" icon={<AddIcon className="w-4 h-4" />} onClick={() => props.onAddLevel(rack.id)}>
          {t('storage.addLevel')}
        </Button>
      </div>

      {rack.levels.length === 0 ? (
        <p className="text-body-sm text-on-surface-variant">{t('storage.noLevelsYet')}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {rack.levels.map((level) => (
            <LevelTile
              key={level.id}
              level={level}
              selected={expandedLevelId === level.id}
              onClick={() => onToggleLevel(level.id)}
              onEdit={() => props.onEditLevel(level)}
              onDelete={() => props.onDeleteLevel(level)}
            />
          ))}
        </div>
      )}

      {openLevel && <LevelPanel level={openLevel} {...props} />}
    </div>
  )
}

interface LevelTileProps {
  level: StorageLevel
  selected: boolean
  onClick: () => void
  onEdit: () => void
  onDelete: () => void
}

/** A level, shown as a tile — the primary way to browse what's on it at a glance. */
function LevelTile({ level, selected, onClick, onEdit, onDelete }: LevelTileProps) {
  const { t } = useTranslation()
  return (
    <div
      className={`group relative border rounded-lg p-3 text-start interactive cursor-pointer ${
        selected ? 'border-primary bg-surface-high' : 'border-outline-variant bg-surface hover:bg-surface-high'
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-body-md font-medium text-on-surface truncate">{level.name}</div>
          {level.description && (
            <div className="text-body-sm text-on-surface-variant truncate mt-0.5">{level.description}</div>
          )}
        </div>
        <div className="flex gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 interactive">
          <IconButton
            icon={<EditIcon className="w-3.5 h-3.5" />}
            label={t('storage.editLevel')}
            variant="standard"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onEdit()
            }}
          />
          <IconButton
            icon={<DeleteIcon className="w-3.5 h-3.5" />}
            label={t('storage.removeLevel')}
            variant="danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
          />
        </div>
      </div>
      <div className="text-body-sm text-on-surface-variant mt-2">
        {level.allocations.length === 0
          ? t('storage.nothingAllocatedYetShort')
          : t('storage.itemCount', { count: level.allocations.length })}
      </div>
    </div>
  )
}

/** The expanded detail for a selected level: the compact icon grid of allocated items. */
function LevelPanel({
  level,
  levelRefs,
  focusLevelId,
  onToggleLevel,
  onAddItem,
  onOpenItem,
  onShowQr,
}: { level: StorageLevel } & ChildProps) {
  const { t } = useTranslation()
  const isFocused = focusLevelId === level.id
  const [sort, setSort] = useAllocationSort()
  const sortedAllocations = useMemo(() => sortAllocations(level.allocations, sort), [level.allocations, sort])

  return (
    <div
      ref={(el) => {
        if (el) levelRefs.current.set(level.id, el)
        else levelRefs.current.delete(level.id)
      }}
      className={`border rounded-lg p-4 transition-colors ${
        isFocused ? 'border-primary ring-1 ring-primary' : 'border-outline-variant'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-body-md font-medium text-on-surface">{level.name}</div>
          {level.description && <div className="text-body-sm text-on-surface-variant mt-0.5">{level.description}</div>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <IconButton
            icon={<QrCodeIcon className="w-4 h-4" />}
            label={t('storage.qrCodeForLevel')}
            variant="standard"
            size="sm"
            onClick={() => onShowQr(level)}
          />
          <Button variant="tonal" size="sm" icon={<AddIcon className="w-4 h-4" />} onClick={() => onAddItem(level.id)}>
            {t('storage.addItem')}
          </Button>
          <IconButton
            icon={<ChevronDownIcon className="w-4 h-4" />}
            label={t('storage.collapseLevel')}
            variant="standard"
            size="sm"
            onClick={() => onToggleLevel(level.id)}
          />
        </div>
      </div>

      {level.allocations.length > 1 && (
        <div className="mb-3">
          <AllocationSortSelect value={sort} onChange={setSort} />
        </div>
      )}

      {level.allocations.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
          {sortedAllocations.map((allocation) => (
            <AllocationIcon key={allocation.id} allocation={allocation} onClick={() => onOpenItem(level.id, allocation)} />
          ))}
        </div>
      )}
    </div>
  )
}
